import React from 'react';

import {
    emoji,
    expression,
    INLINE_BUTTON,
    INLINE_EMOJI,
    INLINE_EXPRESSION,
    INLINE_ICON,
    INLINE_LINK,
    INLINE_MATH,
    icon,
    images,
    math,
} from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import { createPickEmojiPalette, type PaletteCommandDef } from '@gitbook/react-uikit';
import { type Change, isBlockRangeSelection, type TextRange } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import type { IconDefinition } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { useContentReferenceContext } from '../ContentReferences';
import { createPickIconPalette } from '../Icons';
import { useInsertFiles } from '../InsertFilesDialog';
import { createButtonPalette } from './buttons';
import { ContentEditorContext } from './contexts';
import type { InlineControl } from './inline-controls';
import { createLinkPalette } from './link/util';

export type InlineCommandsHookProps = {
    editor: EditorContainer;
};

type InlineCommand = {
    key: InlineControl;
    title: string;
    upgrade?: boolean;
    badge?: PaletteCommandDef['badge'];
    icon: IconDefinition;
    trigger: () => void;
};

export function useInlineCommands(
    props: InlineCommandsHookProps
): (args?: { getRange?: () => TextRange }) => InlineCommand[] {
    const { editor } = props;
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    const hiveClient = useHiveClient();
    const {
        blocks: { withImages },
    } = React.useContext(ContentEditorContext);
    const contentReferenceContext = useContentReferenceContext();
    const insertFiles = useInsertFiles();

    return useEventCallback((args) => {
        const changeOnRange = (callback: (change: Change, range: TextRange) => void) => {
            editor.change((change) => {
                const range = args?.getRange?.() ?? change.value.selection;
                if (isBlockRangeSelection(range)) {
                    return;
                }
                return callback(change, range);
            });
        };
        const commands: InlineCommand[] = [];

        if (withImages) {
            commands.push({
                key: 'inline-image',
                title: 'Inline image',
                icon: Icon.Image,
                trigger: () => {
                    // to make sure focus is not on the editor otherwise the palette will stay open
                    editor.change((change) => change.blur());
                    insertFiles
                        .prompt({
                            title: 'Select image',
                            accept: 'images',
                            acceptURL: true,
                        })
                        .then((fileRefs) => {
                            changeOnRange((change, range) => {
                                fileRefs.forEach((ref) => {
                                    change.insertInlineAtRange(
                                        range,
                                        images.createImageInline(ref)
                                    );
                                });
                                change.focus();
                            });
                        });
                },
            });
        }

        commands.push({
            key: 'button',
            title: 'Button',
            icon: Icon.Button,
            trigger: () => {
                logAnalyticsEvent({
                    type: 'edit_page_document_insert_inline',
                    from: 'inline-palette',
                    inlineType: INLINE_BUTTON,
                });
                return createButtonPalette({
                    editor,
                    getRange: args?.getRange,
                    contentReferenceContext,
                    hiveClient,
                });
            },
        });

        commands.push(
            {
                key: 'emoji',
                title: 'Emoji',
                icon: Icon.EmojiReaction,
                trigger: () => {
                    logAnalyticsEvent({
                        type: 'edit_page_document_insert_inline',
                        from: 'inline-palette',
                        inlineType: INLINE_EMOJI,
                    });
                    return createPickEmojiPalette((code) => {
                        changeOnRange((change, range) => {
                            change.insertInlineAtRange(range, emoji.createEmoji(code)).focus();
                        });
                    });
                },
            },
            {
                key: 'link',
                title: 'Link',
                icon: Icon.Link,
                trigger: () => {
                    logAnalyticsEvent({
                        type: 'edit_page_document_insert_inline',
                        from: 'inline-palette',
                        inlineType: INLINE_LINK,
                    });
                    return createLinkPalette({
                        editor,
                        hiveClient,
                        contentReferenceContext,
                        getRange: args?.getRange,
                        preferredAlignment: 'middle',
                        preferredPlacement: 'left',
                    });
                },
            },
            {
                key: 'inline-math',
                title: 'Math & TeX',
                icon: Icon.MathTeX,
                trigger: () => {
                    logAnalyticsEvent({
                        type: 'edit_page_document_insert_inline',
                        from: 'inline-palette',
                        inlineType: INLINE_MATH,
                    });
                    changeOnRange((change, range) => {
                        change.insertInlineAtRange(range, math.createMathInline()).focus();
                    });
                },
            },
            {
                key: INLINE_ICON,
                title: 'Icon',
                icon: Icon.EmojiFlag,
                trigger: () => {
                    logAnalyticsEvent({
                        type: 'edit_page_document_insert_inline',
                        from: 'inline-palette',
                        inlineType: INLINE_ICON,
                    });
                    return createPickIconPalette((iconName) => {
                        changeOnRange((change, range) => {
                            change.insertInlineAtRange(range, icon.createIcon(iconName)).focus();
                        });
                    });
                },
            },
            {
                key: INLINE_EXPRESSION,
                title: 'Expression',
                icon: Icon.Expression,
                trigger: () => {
                    logAnalyticsEvent({
                        type: 'edit_page_document_insert_inline',
                        from: 'inline-palette',
                        inlineType: INLINE_EXPRESSION,
                    });
                    changeOnRange((change, range) => {
                        const inline = expression.createExpression();
                        change.insertInlineAtRange(range, inline).collapseToStartOf(inline).focus();
                    });
                },
            }
        );

        return commands;
    });
}
